import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Stack from '@mui/material/Stack'
import Input from 'components/Input'
import Checkbox from 'components/Checkbox'
import Icon from 'components/Icon'
import valid from 'card-validator'

export const CARDS = {
  'american-express': 'CardAmericanExpress',
  unionpay: 'CardChinaUnionPay',
  dankort: 'CardDankort',
  'diners-club': 'CardDinersClub',
  discover: 'CardDiscover',
  generic: 'CardGeneric',
  'inter-payments': 'CardInterPayments',
  jcb: 'CardJCB',
  maestro: 'CardMaestro',
  mastercard: 'CardMasterCard',
  uatp: 'CardUATP',
  visa: 'CardVisa',
}

const CreditCardInput = ({ 
  splitName, 
  ccInputLayout, 
  isCorporate,
  onValid, 
  onInvalid,
  hideSave,
}) => {
  const user = useSelector(({ global }) => isCorporate ? null : global.user)
  const fullName = isCorporate ? '' : `${user.first_name} ${user.last_name}`
  const [nameValidity, setNameValidity] = useState(valid.cardholderName(fullName))
  const [numberValidity, setNumberValidity] = useState({})
  const [expiryValidity, setExpiryValidity] = useState({})
  const [cvvValidity, setCVVValidity] = useState({})
  const [name, setName] = useState(fullName)
  const [firstName, setFirstName] = useState(isCorporate ? '' : user.first_name)
  const [lastName, setLastName] = useState(isCorporate ? '' : user.last_name)
  const [number, setNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvv, setCVV] = useState('')
  const [rememberCard, setRememberCard] = useState(false)

  useEffect(() => {
    if (nameValidity.isValid && numberValidity.isValid && expiryValidity.isValid && cvvValidity.isValid) {
      onValid({
        card_number: number,
        card_expiration: expiry?.split('/').reverse().join(''),
        card_expiration_full: `${Math.trunc(new Date().getFullYear() / 100)}${expiry?.split('/')[1]}-${expiry?.split('/')[0]}`,
        card_cvv: cvv,
        card_holder_name: splitName ? `${firstName} ${lastName}` : name,
        card_holder_first_name: firstName,
        card_holder_last_name: lastName,
        save_card: rememberCard,
      })
    } else {
      onInvalid()
    }
  }, [
    nameValidity.isValid,
    numberValidity.isValid,
    expiryValidity.isValid,
    cvvValidity.isValid,
    number,
    expiry,
    cvv,
    firstName,
    lastName,
    name,
    rememberCard,
  ])

  return (
    <Stack
      display="grid"
      gap={2}
    >
      <Stack
        display="grid"
        gap={1}
        gridTemplateColumns={splitName ? {
          sm: 'repeat(2, 1fr)',
        } : null}
      >
        {!splitName && (
          <Input
            label="Name on Card"
            optionalLabel={isCorporate ? undefined : 'if different'}
            autocomplete="cc-name"
            name="ccname"
            value={name}
            onChange={val => {
              setName(val)
              setNameValidity(valid.cardholderName(val))
            }}
          />
        )}
        {splitName && (
          <>
            <Input
              label="First Name on Card"
              autocomplete="cc-given-name"
              name="fname"
              value={firstName}
              onChange={val => {
                setFirstName(val)
                setNameValidity(valid.cardholderName(`${val} ${lastName}`))
              }}
            /> 
            <Input
              label="Last Name on Card"
              autocomplete="cc-family-name"
              name="lname"
              value={lastName}
              onChange={val => {
                setLastName(val)
                setNameValidity(valid.cardholderName(`${firstName} ${val}`))
              }}
            />
          </>
        )}
      </Stack>
      <Stack
        display="grid"
        gridTemplateColumns={ccInputLayout}
        gap={1}
        alignItems="center"
        alignContent="center"
        // maxWidth="450px"
      >
        <Input
          label="Card Number"
          inputmode="numeric"
          autocomplete="cc-number"
          name="cardnumber"
          endAdornment={(
            <Stack
              display="grid"
              justifyContent="flex-end"
              alignItems="center"
              alignContent="center"
              width="60px"
              height="15px"
            >
              <Icon
                icon={CARDS[(numberValidity?.card?.type)] || 'CardGeneric'}
                height="15"
                maxWidth="60px"
              />
            </Stack>
          )}
          removeMaskOnChange
          value={number}
          onChange={val => {
            setNumber(val)
            setNumberValidity(valid.number(val))
            // setExpiryValidity(valid.expirationDate(expiry))
            // setCVVValidity(valid.expirationDate(cvv, numberValidity?.card?.code?.size || 3))
          }}
        />
        <Stack
          display="inline-grid"
          gridTemplateColumns="repeat(2, auto)"
          gap={1}
          alignContent="center"
          alignItems="center"
        >
          <Input
            label="Expires"
            inputmode="numeric"
            autocomplete="cc-exp"
            name="exp-date"
            mask="expiry"
            placeholder="MM/YY"
            value={expiry}
            onChange={val => {
              setExpiry(val)
              setExpiryValidity(valid.expirationDate(val))
              // setNumberValidity(valid.number(number))
              // setCVVValidity(valid.expirationDate(cvv, numberValidity?.card?.code?.size || 3))
            }}
          />
          <Input
            label="CVC/CVV"
            inputmode="numeric"
            autocomplete="cc-csc"
            name="cvc"
            mask={`cvc_${numberValidity?.card?.code?.size || 3}`}
            value={cvv}
            onChange={val => {
              setCVV(val)
              setCVVValidity(valid.cvv(val, numberValidity?.card?.code?.size || 3))
              // setExpiryValidity(valid.expirationDate(expiry))
              // setNumberValidity(valid.number(number))
            }}
          />
        </Stack>
      </Stack>
      {!hideSave && (
        <Checkbox
          label="Remember this card"
          checked={rememberCard}
          onChange={() => setRememberCard(!rememberCard)}
        />
      )}
    </Stack>
  )
}

CreditCardInput.propTypes = {
  isCorporate: PropTypes.bool,
  splitName: PropTypes.bool,
  onValid: PropTypes.func,
  onInvalid: PropTypes.func,
  ccInputLayout: PropTypes.any,
}

CreditCardInput.defaultProps = {
  ccInputLayout: {
    xs: '4fr 3fr',
    md: 'initial',
    lg: '4fr 3fr',
  },
}

export default CreditCardInput
